import { useState } from "react";

import { extractSituacija, fetchOpciDioStatus, recognizeDocument } from "@/api/client";
import { applyRecognizedDocToDraft, applySituacijaToDraft } from "./prefillToDraft";
import { situacijaIsEmpty } from "./situacijaStatus";
import { useIntakeStore } from "./useIntakeStore";

/** Document types that may carry the situacija figures (iskaz table).
 * R3 added the explicit situacija sheet types next to the legacy
 * geodetski_elaborat — a ZETA-style situacija with an iskaz keeps
 * feeding the situacija extractor no matter which of these the
 * classifier picked; a sheet WITHOUT an iskaz simply yields no report
 * and the card's diagnosis says exactly that. */
export const SITUACIJA_TYPES = new Set([
  "geodetski_elaborat",
  "situacija_sira",
  "situacija_uza",
]);

export interface OpciDioParams {
  building_type: string;
  building_subtype?: string | null;
  context?: string | null;
  work_type?: string;
}

/** Per-file result of an ``addPdfFiles`` call — the console row that
 * initiated a targeted upload reads its own outcome from here (the hook's
 * ``errors`` state stays the page-level aggregate). */
export interface PdfUploadOutcome {
  file: string;
  ok: boolean;
  documentType?: string;
  error?: string;
}

/**
 * Shared PDF routing — the same path the landing basket takes: every PDF
 * goes through ``/api/recognize-document``; a recognized geodetski
 * elaborat additionally feeds ``/api/situacija/extract`` so the situacija
 * figures land without the user knowing there are two extractors. Used by
 * the unified sources basket AND the completeness panel's inline upload,
 * so a document added from either place follows one code path.
 */
export function useRecognizeDocs(projectId: string, params: OpciDioParams) {
  const [queue, setQueue] = useState<string[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  /** Recompute opći-dio status from whatever the store now holds — keeps
   *  the completeness panel and the readiness chip in sync. Advisory: a
   *  failed refresh must never block the upload flow. */
  const refreshStatus = async () => {
    const entry = useIntakeStore.getState().get(projectId);
    try {
      const status = await fetchOpciDioStatus({
        ...params,
        situacija: entry.situacija ?? undefined,
        recognized_docs: entry.recognizedDocs,
      });
      useIntakeStore.getState().setOpciDioStatus(projectId, status);
    } catch {
      // advisory
    }
  };

  const addPdfFiles = async (
    files: File[],
    opts?: { expectedType?: string },
  ): Promise<PdfUploadOutcome[]> => {
    setErrors([]);
    const outcomes = await Promise.all(
      files.map(async (file): Promise<PdfUploadOutcome> => {
        setQueue((q) => [...q, file.name]);
        try {
          const doc = await recognizeDocument(file, opts?.expectedType);
          useIntakeStore.getState().addRecognizedDoc(projectId, doc);
          // R2 — hydrate empty draft fields from the recognized metadata
          // (ured / architect+HKA / investitor / parcel). Same merge path
          // as NL: drawing-derived values stay untouchable, everything
          // hydrated carries the amber "provjeri" stamp.
          applyRecognizedDocToDraft(projectId, doc);
          const current = useIntakeStore.getState().get(projectId).situacija;
          const isSituacijaType = SITUACIJA_TYPES.has(doc.document_type);
          // Extract when there is no situacija yet — or when the one we
          // hold is empty (0 parsed figures), so a later sheet that DOES
          // carry the iskaz can still claim the slot. A "nepoznato"
          // classification also gets a deterministic PROBE (the vision
          // classifier misfires on real sheets — Lukin nalaz 13.07.), but
          // may claim the slot only when figures were actually read.
          if (
            (isSituacijaType || doc.document_type === "nepoznato") &&
            (!current || situacijaIsEmpty(current))
          ) {
            try {
              const report = await extractSituacija(file);
              const claims = isSituacijaType || !situacijaIsEmpty(report);
              const held = useIntakeStore.getState().get(projectId).situacija;
              if (
                claims &&
                (!held || (situacijaIsEmpty(held) && !situacijaIsEmpty(report)))
              ) {
                useIntakeStore.getState().setSituacija(projectId, report);
                // Figures → form fields (fill-empty-only, amber stamp) —
                // otherwise the parsed values never reach the Provjeri form.
                applySituacijaToDraft(projectId, report);
              }
            } catch (e) {
              // The doc is still recognized — but on a situacija-typed sheet
              // the user must SEE that the figures are missing, not a silent
              // success. (A failed probe on "nepoznato" is not news.)
              if (isSituacijaType) {
                setErrors((prev) => [
                  ...prev,
                  `${file.name}: iskaz situacije nije pročitan (${
                    e instanceof Error ? e.message : String(e)
                  })`,
                ]);
              }
            }
          }
          return { file: file.name, ok: true, documentType: doc.document_type };
        } catch (e) {
          const message = e instanceof Error ? e.message : String(e);
          setErrors((prev) => [...prev, `${file.name}: ${message}`]);
          return { file: file.name, ok: false, error: message };
        } finally {
          setQueue((q) => q.filter((n) => n !== file.name));
        }
      }),
    );
    await refreshStatus();
    return outcomes;
  };

  return { addPdfFiles, refreshStatus, queue, errors };
}
